import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Input, InputNumber, message, Space } from "antd";
import axios from "../api/axios";

const API_URL = "/costfactors";

const COLORS = {
  border: "#DCE7F3",
  text: "#1F2937",
};

const CostsPage = () => {
  const [costFactors, setCostFactors] = useState([]);
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchCostFactors = async () => {
    setLoading(true);

    try {
      const [res, busesRes] = await Promise.all([axios.get(API_URL), axios.get("/buses")]);
      setCostFactors(res.data);
      setBuses(busesRes.data || []);
    } catch (err) {
      message.error("Fehler beim Laden der Kostenfaktoren.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCostFactors();
    // eslint-disable-next-line
  }, []);

  const handleSave = async (values) => {
    try {
      if (editing) {
        await axios.put(`${API_URL}/${editing.name}`, values);

        message.success("Kostenfaktor aktualisiert.");
      }

      setModalOpen(false);
      setEditing(null);
      fetchCostFactors();
    } catch (err) {
      message.error("Speichern fehlgeschlagen.");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      width: 220,
    },
    {
      title: "Label",
      dataIndex: "label",
      width: 260,
    },
    {
      title: "Wert",
      dataIndex: "value",
      width: 160,
      render: (v) =>
        typeof v === "number" ? v.toLocaleString("de-DE") : v,
    },
    {
      title: "Einheit",
      dataIndex: "einheit",
      width: 140,
    },
    {
      title: "Aktionen",
      key: "action",
      width: 150,
      render: (_, record) => (
        <Button
          size="small"
          onClick={() => {
            setEditing(record);
            setModalOpen(true);
          }}
        >
          Bearbeiten
        </Button>
      ),
    },
  ];

  const vehicleRates = [
    ["customBusCostPerKm", "bus_km", "Bus €/km", 1.19],
    ["customBusCostPerHour", "bus_hour", "Bus €/Std.", 45],
    ["customAttendantCostPerKm", "beifahrer_km", "Begleitung €/km", 0.85],
    ["customAttendantCostPerHour", "beifahrer_hour", "Begleitung €/Std.", 32],
    ["customFixedCostPerTour", "bus_fixed", "Fixkosten €/Tour", 0],
  ];

  const busesWithDifferentCosts = buses.filter((bus) =>
    bus.customCostsEnabled && vehicleRates.some(([field, key, , fallback]) => {
      const standard = costFactors.find((factor) => factor.name === key)?.value ?? fallback;
      return bus[field] != null && Number(bus[field]) !== Number(standard);
    })
  );

  const rateColumns = vehicleRates.map(([field, key, title, fallback]) => ({
    title,
    key: field,
    render: (_, bus) => {
      const standard = costFactors.find((factor) => factor.name === key)?.value ?? fallback;
      const individual = bus.customCostsEnabled && bus[field] != null;
      const value = individual ? bus[field] : standard;
      return <span>{Number(value).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        {individual ? " (individuell)" : " (Standard)"}</span>;
    },
  }));

  const initialValues = editing
    ? { ...editing }
    : { name: "", label: "", value: 0, einheit: "" };

  return (
    <div className="costs-page">
      <h2 className="app-page-title">Kostenfaktoren</h2>
      <p>
        Diese Werte gelten für alle Fahrzeuge ohne aktivierte individuelle Kosten.
        Fixkosten werden je belegter Tour berechnet.
      </p>

      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Table
          dataSource={costFactors}
          columns={columns}
          rowKey="name"
          loading={loading}
          bordered
          pagination={false}
        />
        <h3>Wirksame Fahrzeugkosten je Bus</h3>
        <p>Hier erscheinen nur Busse, deren wirksame Fahrzeugkosten von den Standardkosten abweichen.</p>
        <Table rowKey="id" dataSource={busesWithDifferentCosts} loading={loading} bordered pagination={false}
          locale={{ emptyText: "Keine Busse mit abweichenden Fahrzeugkosten vorhanden." }}
          scroll={{ x: 1000 }} columns={[
            { title: "Bus", dataIndex: "name" },
            ...rateColumns,
          ]} />
      </Space>

      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title="Kostenfaktor bearbeiten"
        footer={null}
        destroyOnClose
      >
        <Form
          layout="vertical"
          initialValues={initialValues}
          onFinish={handleSave}
        >
          <Form.Item name="name" label="Name">
            <Input disabled />
          </Form.Item>

          <Form.Item name="label" label="Label">
            <Input disabled />
          </Form.Item>

          <Form.Item
            name="value"
            label="Wert"
            rules={[{ required: true, type: "number" }]}
          >
            <InputNumber
              style={{ width: "100%" }}
              step={0.01}
              min={0}
              decimalSeparator=","
              inputMode="decimal"
            />
          </Form.Item>

          <Form.Item name="einheit" label="Einheit">
            <Input disabled />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Speichern
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <style>{`
        .costs-page h2 {
          margin-top: 0;
          margin-bottom: 18px;
          color: ${COLORS.text};
          font-weight: 700;
        }

        .costs-page .ant-table-thead > tr > th {
          white-space: nowrap;
          background: #F4FBFC !important;
          border-bottom: 1px solid ${COLORS.border} !important;
          color: ${COLORS.text} !important;
          font-weight: 700 !important;
        }

        .costs-page .ant-table-tbody > tr > td {
          vertical-align: middle;
          line-height: 1.45;
        }
      `}</style>
    </div>
  );
};

export default CostsPage;
